import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { isNonNullish } from '@epicuro-next/utilities';
import { FloatRange, ProductPriceRanges } from 'graphql-api/gql-api-interfaces';

const selector = 'atx-product-prices';

@DynamicComponentRegister(selector, true)
@Component({
  selector,
  templateUrl: './product-prices.component.html',
  styleUrls: ['./product-prices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductPricesComponent {
  public isSearchV2Enabled = input<boolean>(false);
  public prices = input<ProductPriceRanges | null>(null);
  public showWspdPrice = input<boolean>(true);
  public showWholeSalePrices = input(true);
  public showRrpPrices = input(true);
  public showMarkup = input(false);
  public isLoading = input(false);

  public readonly rrp = computed(() =>
    this.getPriceRangeValues(this.prices()?.rrp ?? {}),
  );
  public readonly wsp = computed(() =>
    this.getPriceRangeValues(this.prices()?.wsp ?? {}),
  );
  public readonly markup = computed(() =>
    isNonNullish(this.prices()?.markup) ? { min: this.prices()?.markup } : {},
  );

  private readonly shouldShowWspd = computed(() => {
    if (this.isSearchV2Enabled()) {
      return this.checkShowWspdV2(this.prices()?.wsp, this.prices()?.wspd);
    }

    return this.checkShowWspdV1(this.wsp(), this.prices()?.wspd);
  });

  public readonly wspd = computed(() => {
    if (this.showWspdPrice() && this.shouldShowWspd()) {
      return this.getPriceRangeValues(this.prices()?.wspd as FloatRange);
    }
    return null;
  });

  /**
   * The wspd should be shown only when there's actually a discount.
   * Scenarios in which there's no discount or data is wrong:
   * - wspd.min is not defined
   * - wspd.min is bigger than wspd.max
   * - wspd.min is bigger than wsp.min and the wspd.max bigger than wsp.max or wsp.max is not even defined
   * - wspd.max is bigger than wsp.max or wsp.min (if wsp.max is not defined)
   *
   * @param wsp
   * @param wspd
   */
  private checkShowWspdV1(wsp: FloatRange, wspd?: FloatRange | null) {
    // min should always be defined and if max is defined, it should be bigger than min.
    if (!wspd?.min || !wsp.min || (wspd.max && wspd.min > wspd.max)) {
      return false;
    }

    const minIsLowerThanWSPMin = wspd.min < wsp.min;

    // if at least wspd.max is lower than wsp.max then the discount is valid, otherwise it depends on the min values
    if (wspd.max && wsp.max) {
      return minIsLowerThanWSPMin || wspd.max < wsp.max;
    }

    // if the wspd.min is lower than wsp.min and the wspd.max is defined then it must be lower than the wsp.min when if the max is not defined; and could be equal to the wsp.max if both are defined.
    if (
      minIsLowerThanWSPMin &&
      (!wspd.max || wsp.max === wspd.max || (!wsp.max && wspd.max < wsp.min))
    ) {
      return true;
    }

    return false;
  }
  private checkShowWspdV2(wsp?: FloatRange | null, wspd?: FloatRange | null) {
    if (!wspd || !wsp) {
      return false;
    }
    // min should always be defined and if max is defined, it should be bigger than min.
    if (!wspd?.min || !wsp.min || (wspd.max && wspd.min > wspd.max)) {
      return false;
    }

    return wspd.min < wsp.min || (wspd?.max && wspd.max < (wsp?.max ?? 0));
  }

  private getPriceRangeValues({ min, max, currency }: FloatRange): {
    min: number;
    max?: number;
    currency?: string | null;
  } {
    if (max && min && min !== max) {
      return { min, max, currency };
    }
    return { min: min as number, currency };
  }
}
